import React, { useState, useCallback } from 'react';
import { useClientAccess } from '../hooks/useClientAccess';
import { useI18n } from '../contexts/I18nContext';
import { ConfigTemplatesTable } from '../components/configTemplates/ConfigTemplatesTable';
import { CreateConfigTemplateModal } from '../components/configTemplates/CreateConfigTemplateModal';
import { ConfigTemplateCreate } from '../types/configTemplate';
import '../styles/config-templates.css';

/**
 * Configuration templates, schema v2: one template per device, owned by
 * ANEP or by one client.
 *
 * Who does what: every role reads (a client's users see their own
 * templates plus the ANEP ones); ANEP roles and a client's admin create
 * and edit; SuperAdmin assigns a template to clients or devices in bulk.
 */
export const ConfigTemplatesPage: React.FC = () => {
  const { canWriteTemplates: canEdit, canAssignTemplates: canAssign } = useClientAccess();
  const { formatTime, t } = useI18n();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [refreshTemplates, setRefreshTemplates] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  const handleTemplateCreated = (_template: ConfigTemplateCreate) => {
    setRefreshTemplates((prev) => prev + 1);
    setLastUpdated(new Date());
  };

  const handleRefresh = useCallback(() => {
    setRefreshTemplates((prev) => prev + 1);
    setLastUpdated(new Date());
  }, []);

  return (
    <div className="page config-templates-page">
      <div className="page-header">
        <div className="page-title">
          <h1>{t('configTemplates.title')}</h1>
          <p>{t('configTemplates.subtitle')}</p>
        </div>
        <div className="page-actions">
          <span className="last-updated">{t('configTemplates.updatedAt', { time: formatTime(lastUpdated) })}</span>
          <button type="button" className="refresh-button" onClick={handleRefresh}>
            {t('common.refresh')}
          </button>
          {canEdit && (
            <button type="button" className="create-template-btn" data-testid="btn-create-template" onClick={() => setShowCreateModal(true)}>
              <span className="add-type-icon" aria-hidden="true">+</span>
              {t('configTemplates.createButton')}
            </button>
          )}
        </div>
      </div>

      <div className="page-content config-templates-content">
        <ConfigTemplatesTable
          onTemplateSelect={() => {}}
          refreshTrigger={refreshTemplates}
          canEdit={canEdit}
          canAssign={canAssign}
        />
      </div>

      <CreateConfigTemplateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onTemplateCreated={handleTemplateCreated}
      />
    </div>
  );
};
